import { TicketInfo } from '@Config/ticket-form-types'
import { TicketSummary } from '../ticket-summary/ticket-summary'
import { TicketFormSummary } from '../ticket-form-summary/ticket-form-summary'

import './styles.scss'
import { KenoScoringBreakdown } from '@interaction-gaming/game-data-service-browser-client'
import { useCallback, useState } from 'react'
import {
  ExtraDrawsAddOnResult,
  MiniGameAddOnResult,
  MultiplierAddOnResult,
} from '@Config/draw-results-types'
import { MatchStatus } from '../number-selection-summary/number-selection-summary-types'
import { ReceiptBreakdownDrawer } from '../receipt-breakdown/receipt-breakdown-drawer'
import { AddOnTypes } from '@Config/types'
import Analytics, { AnalyticEvents } from '@Util/analytics'
import { mapAddonDetails } from '../ticket-form-summary/utils'

export type TicketListViewProps = {
  ticketInfo: TicketInfo
  drawResults: ListResult[]
  matchColors: MatchColorConfig[]
}

export type MatchColorConfig = {
  name: string
  color?: string
}

export type ListResult = {
  type: 'COMPLETED' | 'PENDING'
  drawDate: string | null
  drawNumber: number
  extraDrawAddOnResult: ExtraDrawsAddOnResult | undefined
  iconUrl: string | null
  miniGameAddOnResult: MiniGameAddOnResult | undefined
  multiplierAddOnResult: MultiplierAddOnResult | undefined
  numberSelections: ListViewMatchDetails[]
  scoringBreakdown: KenoScoringBreakdown | null
  totalWinAmountInCents: number
}

export type ListViewMatchDetails = {
  number: number
  matchStatus: MatchStatus
  addOnName: string | null
}

export type AddonResultDetails = {
  name: string
  color: string | null
  type: AddOnTypes
}

const CLASS_NAME = `ticket-list-view`

export const TicketListView = (props: TicketListViewProps) => {
  const [selectedResult, setSelectedResult] = useState<ListResult | null>()
  const onDrawerStateChange = (open: boolean) => {
    !open && setSelectedResult(null)
  }

  const totalWinnings = props.drawResults.reduce((acc, curr) => {
    if (curr.type === 'COMPLETED') {
      return acc + curr.totalWinAmountInCents
    }
    return acc
  }, 0)

  const pendingGames = props.drawResults.reduce((acc, curr) => {
    if (curr.type === 'PENDING') {
      return acc + 1
    }
    return acc
  }, 0)

  const addOnDetails = useCallback(() => {
    return mapAddonDetails(
      props.ticketInfo.multiplierAddOnSelection,
      props.ticketInfo.extraDrawAddOnSelection,
      props.ticketInfo.miniGameAddOnSelection,
      props.matchColors,
    )
  }, [props.ticketInfo, props.matchColors])

  const renderDrawResults = () => {
    const elements = props.drawResults.map((result) => {
      return (
        <TicketFormSummary
          key={`${CLASS_NAME}_${result.drawNumber}`}
          type={result.type}
          addOnDetails={addOnDetails()}
          drawNumber={result.drawNumber}
          drawDate={result.drawDate}
          numbersPerRow={5}
          numberSelections={result.numberSelections}
          totalWinnings={result.totalWinAmountInCents}
          onClick={() => {
            setSelectedResult(result)
            Analytics.trackDrawing(AnalyticEvents.RECEIPT_SUMMARY_VIEW, result.drawNumber)
          }}
          iconUrl={result.iconUrl}
          multiplier={result.multiplierAddOnResult?.value}
        />
      )
    })

    return <div className={`${CLASS_NAME}__ticket-summary-list`}>{elements}</div>
  }

  return (
    <div className={`${CLASS_NAME}`}>
      <TicketSummary totalWinnings={totalWinnings} pendingGames={pendingGames} />
      {renderDrawResults()}
      {selectedResult ? (
        <ReceiptBreakdownDrawer
          kenoResult={selectedResult}
          onOpenChange={onDrawerStateChange}
          addonDetails={addOnDetails()}
          ticketInfo={props.ticketInfo}
        />
      ) : null}
    </div>
  )
}
